<template>
  <div class="gala-card">
    <div class="gala-image">
      <img 
        v-if="gala.photo_hash" 
        :src="`${$BACKEND_HOST}/api/gala/photo/${gala.photo_hash}`" 
        :alt="gala.name"
        @error="handleImageError"
        loading="lazy"
      >
      <div v-else class="no-image">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21,15 16,10 5,21"/>
        </svg>
      </div>
      <div class="gala-date-badge">
        {{ formatDate(gala.event_date) }}
      </div>
      <div v-if="gala.registration_deadline" class="deadline-badge" :class="{ 'deadline-urgent': isDeadlineSoon(gala.registration_deadline) }">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
        Határidő: {{ formatDate(gala.registration_deadline) }}
      </div>
    </div>
    
    <div class="gala-content">
      <div class="gala-header">
        <h3>{{ gala.name }}</h3>
        <div v-if="showPrice && gala.ticket_price > 0" class="ticket-price">
          <span class="price-value">{{ formatPrice(gala.ticket_price) }} Ft</span>
        </div>
      </div>
      
      <div class="gala-details">
        <div class="gala-location">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <span>{{ gala.location }}</span>
        </div>
        
        <div class="gala-description" v-if="gala.description">
          <p>{{ gala.description }}</p>
        </div>
        
        <!-- Ticket Availability Info -->
        <div v-if="showTicketInfo" class="ticket-availability">
          <div class="availability-item" v-if="gala.max_tickets && gala.max_tickets > 0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
            <span>{{ getRemainingTickets(gala) }} / {{ gala.max_tickets }} jegy elérhető</span>
          </div>
          <div class="availability-item" v-else-if="gala.max_tickets === 0">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
            <span>Korlátlan jegyek</span>
          </div>
          <div v-if="myGroupTicketInfo && myGroupTicketInfo.primary_group_name" class="availability-item primary-group-info">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span>
              {{ myGroupTicketInfo.primary_group_name }}: 
              {{ myGroupTicketInfo.remaining }} / {{ myGroupTicketInfo.quota }} jegy
            </span>
          </div>
        </div>
      </div>

      <slot name="card-actions"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GalaCard',
  props: {
    gala: {
      type: Object,
      required: true
    },
    showPrice: {
      type: Boolean,
      default: true
    },
    showTicketInfo: {
      type: Boolean,
      default: true
    },
    ticketStats: {
      type: Object,
      default: () => ({})
    },
    myGroupTicketInfo: {
      type: Object,
      default: null
    }
  },
  methods: {
    handleImageError(event) {
      event.target.style.display = 'none';
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('hu-HU');
    },
    
    formatPrice(price) {
      return Math.round(price).toLocaleString('hu-HU');
    },
    
    getRemainingTickets(gala) {
      if (!gala.max_tickets || gala.max_tickets === 0) return gala.max_tickets;
      
      // Use provided ticket stats or calculate from gala data
      const soldTickets = this.ticketStats[gala.id]?.sold_tickets || 0;
      const remaining = Math.max(0, gala.max_tickets - soldTickets);
      return remaining;
    },
    
    isDeadlineSoon(deadlineString) {
      if (!deadlineString) return false;
      
      const deadline = new Date(deadlineString);
      const now = new Date();
      const diffTime = deadline.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Consider "soon" if within 3 days
      return diffDays <= 3 && diffDays >= 0;
    }
  }
};
</script>

<style scoped>
.gala-card {
  background: var(--color-background-mute);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  transition: all 0.3s ease;
  overflow: hidden;
}

.gala-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.gala-image {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: var(--color-background);
}

.gala-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gala-card:hover .gala-image img {
  transform: scale(1.05);
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  background: var(--color-background);
}

.gala-date-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.gala-content {
  padding: 1.5rem;
}

.gala-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.gala-header h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.ticket-price {
  background: var(--primary-blue);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
}

.price-value {
  font-weight: 700;
}

.gala-details {
  margin-bottom: 1.5rem;
}

.gala-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
}

.gala-location svg {
  flex-shrink: 0;
}

.gala-description p {
  color: var(--color-text);
  line-height: 1.5;
  font-size: 0.95rem;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.deadline-badge {
  position: absolute;
  top: 3.5rem;
  right: 1rem;
  background: rgba(251, 191, 36, 0.9);
  color: #92400e;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.deadline-badge.deadline-urgent {
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border-color: rgba(239, 68, 68, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

.ticket-availability {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
}

.availability-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 500;
}

.availability-item svg {
  flex-shrink: 0;
  color: #16a34a;
}
</style>